/**
 * SuperAdminLayout - Super Admin Dashboard Layout
 *
 * Purpose: Layout component for super admin pages with sidebar and top bar
 * Dependencies: flowbite-react, react-router-dom, @core/auth, @design-system/components
 * Exports: SuperAdminLayout (default)
 * Side Effects: React rendering, localStorage (sidebar state)
 * Safety: 🟡 RESTRICTED - Layout component affecting routing
 *
 * Agent Notes:
 * - Uses Flowbite design system components
 * - Includes sidebar navigation and top bar
 * - Responsive with mobile drawer
 * - Supports dark mode toggle
 * - 2026-04-10: Main scroll region uses `app-shell-disappearing-scroll` (root CHANGELOG [2.2.354]).
 * - 2026-04-26: Added AiAssistantButton (top of sidebar) + AiAssistantPanel (right slide-out); `main` uses **`id="main-content"`** for skip-link and selection→Ask AI. **⌘⇧A / Ctrl⇧A** toggles the AI panel.
 * - 2026-01-26: Flowbite integration
 */

import { ReactNode, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Sidebar } from '../components/Navigation/Sidebar';
import { AiAssistantButton } from '../components/AiAssistant/AiAssistantButton';
import { AiAssistantPanel } from '../components/AiAssistant/AiAssistantPanel';
import { AssistantSelectionToolbar } from '../components/AiAssistant/AssistantSelectionToolbar';
import { subscribeAssistantOpenWithPrefill } from '../components/AiAssistant/assistantPrefillBridge';
import { TopBar } from '../components/Navigation/TopBar';
import { ToastContainer } from '../components/Feedback/ToastContainer';
import { useAuth, useCurrentUser } from '@core/auth';
import { useTenant } from '@core/tenant';
import { adminNavItems } from '../../config/nav';
import {
  HomeIcon,
  UsersIcon,
  BuildingOfficeIcon,
  Cog6ToothIcon,
  ShieldCheckIcon,
  AdjustmentsHorizontalIcon,
  FlagIcon,
} from '@heroicons/react/24/outline';

interface SuperAdminLayoutProps {
  children: ReactNode;
}

// Map icon names from nav.ts to Heroicon components
const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  shield: ShieldCheckIcon,
  'office-building': BuildingOfficeIcon,
  users: UsersIcon,
  adjustments: AdjustmentsHorizontalIcon,
  home: HomeIcon,
  cog: Cog6ToothIcon,
  flag: FlagIcon,
};

export function SuperAdminLayout({ children }: SuperAdminLayoutProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { user } = useCurrentUser();
  const { currentTenant } = useTenant();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [aiPanelOpen, setAiPanelOpen] = useState(false);
  const [docTitle, setDocTitle] = useState(() =>
    typeof document !== 'undefined' ? document.title : ''
  );

  const aiPageContext = useMemo(
    () => ({
      path: `${location.pathname}${location.search}`,
      title: docTitle,
    }),
    [location.pathname, location.search, docTitle]
  );

  useEffect(() => {
    const id = requestAnimationFrame(() => setDocTitle(document.title));
    return () => cancelAnimationFrame(id);
  }, [location.pathname, location.search]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const mod = e.metaKey || e.ctrlKey;
      if (mod && e.shiftKey && (e.key === 'a' || e.key === 'A')) {
        e.preventDefault();
        setAiPanelOpen((open) => !open);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    return subscribeAssistantOpenWithPrefill(() => {
      setAiPanelOpen(true);
    });
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Convert nav items from nav.ts to Sidebar format with icon components
  const navItems = adminNavItems.map((item) => ({
    path: item.path,
    label: item.label,
    icon: item.icon ? iconMap[item.icon] || HomeIcon : HomeIcon,
  }));

  return (
    <ToastContainer>
      <div className="flex h-screen bg-gray-50 dark:bg-gray-900">
        {/* Sidebar */}
        <Sidebar
          userRole="super_admin"
          activePath={location.pathname}
          items={navItems}
          user={
            user
              ? {
                  name: user.name,
                  email: user.email,
                  avatar_url: user.avatar_url,
                }
              : undefined
          }
          onLogout={handleLogout}
          collapsed={sidebarCollapsed}
          onToggleCollapse={setSidebarCollapsed}
          customHeader={
            <div className="mb-3">
              <AiAssistantButton
                collapsed={sidebarCollapsed}
                isOpen={aiPanelOpen}
                onClick={() => setAiPanelOpen((v) => !v)}
              />
            </div>
          }
        />

        {/* Main content area - responsive margin based on sidebar state */}
        <div
          className="flex flex-1 flex-col overflow-hidden transition-all duration-300"
          style={{
            marginLeft: sidebarCollapsed ? '4rem' : '16rem',
          }}
        >
          {/* Top Bar */}
          <TopBar
            user={
              user
                ? {
                    name: user.name,
                    email: user.email,
                    avatar_url: user.avatar_url,
                  }
                : undefined
            }
            tenantName={currentTenant?.name}
            userMenuWorkspaceTo="/super-admin/settings"
          />

          {/* Main content */}
          <main
            id="main-content"
            role="main"
            className="app-shell-disappearing-scroll flex-1 overflow-y-auto bg-gray-50 p-6 dark:bg-gray-900"
          >
            <div className="mx-auto max-w-7xl">{children}</div>
          </main>
        </div>

        {/* AI Assistant Slide-out Panel */}
        <AiAssistantPanel
          isOpen={aiPanelOpen}
          pageContext={aiPageContext}
          onClose={() => setAiPanelOpen(false)}
        />
        <AssistantSelectionToolbar />
      </div>
    </ToastContainer>
  );
}
